import { memo, RefObject, useEffect, useMemo, useRef, useState } from "react";

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  HeaderGroup,
  Row,
  RowData,
  TableOptions,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { twJoin, twMerge } from "tailwind-merge";

import { MatrixTableRow } from "src/matrixTableNode/MatrixTable";
import { MatrixTableError } from "src/matrixTableNode/MatrixTableNodeEditor";
import { SortableRow } from "src/matrixTableNode/SortableRow";
import { useMatrixTableNavActions } from "src/matrixTableNode/stores";
import { useKeyboardNavigation } from "src/matrixTableNode/useKeyboardNavigation";

// We actually expect any functions here and can not be explicitly typed
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TableActions = Record<string, (...args: any[]) => void>;

declare module "@tanstack/table-core" {
  // eslint-disable-next-line unused-imports/no-unused-vars
  interface TableMeta<TData extends RowData> {
    actions?: TableActions;
  }
}

type Props = {
  data: MatrixTableRow[];
  columns: TableOptions<MatrixTableRow>["columns"];
  actions: TableActions;
  errors: MatrixTableError[];
  readOnly: boolean;
  containerRef: RefObject<HTMLElement | null>;
};

const TABLE_CLASS_NAMES =
  "w-full table-auto border-collapse border-spacing-0 text-gray-800";
const HEADER_CLASS_NAMES =
  "sticky top-0 bg-opacity-100 bg-white font-inter-medium-12px text-left z-20";

const MatrixTableCompBase = ({
  containerRef,
  data,
  columns,
  actions,
  errors,
  readOnly,
}: Props) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeRow, setActiveRow] = useState<Row<MatrixTableRow> | null>(null);
  const { hoverCell, setErrors } = useMatrixTableNavActions();

  // Customising the pointer sensor to only activate when the mouse moves by 1 pixel
  // This ensures that context menu opens on click and only on drag the overlay is rendered
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 1,
    },
  });
  const sensors = useSensors(pointerSensor);

  useEffect(() => {
    setErrors(errors);
  }, [errors, setErrors]);

  const tableInstance = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    defaultColumn: { size: undefined },
    meta: { actions, readonly: readOnly },
    columnResizeMode: "onChange",
    enableColumnResizing: true,
    onColumnSizingChange: (updater) => {
      if (typeof updater === "function") {
        const oldSizing = tableInstance.getState().columnSizing;
        const newSizing = updater(oldSizing);

        tableInstance.setState((state) => ({
          ...state,
          columnSizing: newSizing,
        }));
      }
    },
  });
  const { getHeaderGroups } = tableInstance;

  const headerGroups = getHeaderGroups();
  const rows = tableInstance.getRowModel().rows;

  const renderHeaderGroup = (headerGroup: HeaderGroup<MatrixTableRow>) => {
    return (
      <tr
        key={headerGroup.id}
        className={twMerge(HEADER_CLASS_NAMES, "[&>th:first-child]:z-[17]")}
      >
        {headerGroup.headers.map((column) => {
          const ctx = column.getContext();
          const sticky = column.column.columnDef.meta?.sticky;

          return (
            <th
              key={column.id}
              className={twJoin("p-0", sticky === "left" && "sticky left-0")}
              colSpan={column.colSpan}
              style={{
                minWidth: column.getSize(),
                maxWidth: ctx.column.columnDef.maxSize,
                width: column.getSize(),
              }}
            >
              {flexRender(column.column.columnDef.header, column.getContext())}
            </th>
          );
        })}
      </tr>
    );
  };

  const renderRow = (row: Row<MatrixTableRow>) => {
    return (
      <SortableRow key={row.id} row={row}>
        {row.getVisibleCells().map((cell) => {
          const sticky = cell.column.columnDef.meta?.sticky;
          return (
            // h-[inherit] & p-0 allow the content inside td to take full width/height
            <td
              key={cell.id}
              className={twJoin(
                "relative h-[inherit] p-0",
                sticky === "left" && "sticky left-0",
              )}
              style={{
                width: cell.column.getSize(),
                maxWidth: cell.column.getSize(),
                minWidth: cell.column.columnDef.minSize,
              }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          );
        })}
      </SortableRow>
    );
  };

  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => scrollContainerRef.current,
    count: rows.length,
    estimateSize: () => 36,
    overscan: 10,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalRowSize = rowVirtualizer.getTotalSize();
  // Dynamic top and botton padding is added to the table content to maintain the correct scroll bar size and position
  const paddingTop = virtualRows?.[0]?.start || 0;
  const paddingBottom =
    totalRowSize - (virtualRows?.[virtualRows.length - 1]?.end || 0);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over) {
      const oldIndex = data.findIndex(
        (row) => row.row_predicate.id === active.id,
      );
      const newIndex = data.findIndex(
        (row) => row.row_predicate.id === over.id,
      );
      actions.reorderRowPredicates(oldIndex, newIndex);
    }
    setActiveRow(null);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active, activatorEvent } = event;

    // Trigger focus so we can acquire the node lock
    (activatorEvent.target as HTMLElement)?.closest("button")?.focus();

    setActiveRow(
      tableInstance
        .getRowModel()
        .rows.find((row) => row.original.row_predicate.id === active.id) ??
        null,
    );
  };

  const rowIds = useMemo(() => data.map((row) => row.row_predicate.id), [data]);

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
        <div
          ref={scrollContainerRef}
          className="decideScrollbar relative isolate h-full overflow-auto"
          onMouseOut={() => hoverCell(null)}
        >
          <table className={TABLE_CLASS_NAMES}>
            <thead>{headerGroups.map(renderHeaderGroup)}</thead>
            <tbody>
              {paddingTop > 0 && (
                <tr>
                  <td style={{ height: `${paddingTop}px` }} />
                </tr>
              )}
              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index];
                return renderRow(row);
              })}

              {paddingBottom > 0 && (
                <tr>
                  <td style={{ height: `${paddingBottom}px` }} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SortableContext>
      <DragOverlay>
        {activeRow && (
          <table className={TABLE_CLASS_NAMES}>
            <tbody>{renderRow(activeRow)}</tbody>
          </table>
        )}
      </DragOverlay>
      {!readOnly && (
        <KeyboardNavigationController
          columnsCount={columns.length}
          containerRef={containerRef}
          rowsCount={rows.length}
        />
      )}
    </DndContext>
  );
};

export const MatrixTableComp = memo(MatrixTableCompBase);

const KeyboardNavigationController = ({
  containerRef,
  rowsCount,
  columnsCount,
}: {
  containerRef: RefObject<HTMLElement | null>;
  rowsCount: number;
  columnsCount: number;
}) => {
  useKeyboardNavigation(containerRef, {
    rowsLength: rowsCount,
    columnsLength: columnsCount,
  });
  return null;
};
